/**
 * documentCache.js
 *
 * Framework-agnostic localStorage LRU cache for instant document rendering.
 *
 * A single localStorage key (`cache_documents_<projectShortName>`) holds a map
 * of `{ [documentId]: entry }`, where each entry is:
 *   { documentId, documentData, documentInfo, lastAccessedAt, updatedAt }
 *
 * Every localStorage access is wrapped in try/catch and degrades to a no-op so
 * this module never throws (private mode, quota exceeded, disabled storage, …).
 */

const PROJECT_SHORT_NAME = "mileslide";
export const CACHE_KEY = `cache_documents_${PROJECT_SHORT_NAME}`;

// Maximum number of documents kept in the cache (least-recently-accessed evicted).
const MAX_CACHED_DOCUMENTS = 5;

// Legacy key names from earlier iterations; removed once on module load.
const LEGACY_CACHE_KEYS = [
  "cached_documents",
  "document_cache",
  "mileslide_document_cache",
];

const now = () => Date.now();

const isBrowser = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

/** Read the full cache map. Never throws; returns {} on any failure. */
function readCache() {
  if (!isBrowser()) return {};
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
}

/** Persist the full cache map. Never throws; degrades to a no-op on failure. */
function writeCache(cache) {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    // Quota exceeded / serialization issue — drop silently.
  }
}

/** An entry is renderable only when it carries the content payload. */
function hasRenderablePayload(entry) {
  return Boolean(entry && entry.documentData);
}

/** Evict least-recently-accessed entries until the cache fits the LRU limit. */
function enforceLruLimit(cache) {
  const ids = Object.keys(cache);
  if (ids.length <= MAX_CACHED_DOCUMENTS) return cache;

  // Sort by lastAccessedAt ascending (oldest first) and drop the overflow.
  const sorted = ids.sort(
    (a, b) => (cache[a]?.lastAccessedAt || 0) - (cache[b]?.lastAccessedAt || 0)
  );
  const overflow = sorted.slice(0, ids.length - MAX_CACHED_DOCUMENTS);
  overflow.forEach((id) => {
    delete cache[id];
  });
  return cache;
}

/**
 * Cheap, NON-mutating existence check — safe to call during render.
 * Returns true only when a renderable entry (with content payload) exists.
 */
export function hasCachedDocument(id) {
  if (!id) return false;
  const cache = readCache();
  return hasRenderablePayload(cache[id]);
}

/**
 * Returns the entry for `id`, bumping its `lastAccessedAt` and persisting.
 * A cache read counts as an access (drives LRU). Returns null when missing.
 */
export function getCachedDocument(id) {
  if (!id) return null;
  const cache = readCache();
  const entry = cache[id];
  if (!entry) return null;

  entry.lastAccessedAt = now();
  cache[id] = entry;
  writeCache(cache);
  return entry;
}

/**
 * Upsert a cache entry. Undefined fields fall back to the existing entry, so a
 * partial update (only documentInfo, or only documentData) is non-destructive.
 * Refreshes timestamps, then enforces the LRU limit.
 */
export function setCachedDocument(id, { documentData, documentInfo } = {}) {
  if (!id) return;
  const cache = readCache();
  const existing = cache[id] || {};
  const timestamp = now();

  cache[id] = {
    documentId: id,
    documentData: documentData !== undefined ? documentData : existing.documentData,
    documentInfo: documentInfo !== undefined ? documentInfo : existing.documentInfo,
    lastAccessedAt: timestamp,
    updatedAt: timestamp,
  };

  enforceLruLimit(cache);
  writeCache(cache);
}

/**
 * Returns `{ documentData, documentInfo }` for `id`, or null when no entry.
 * Does NOT mutate the cache (no access bump) — purely a read for seeding state.
 */
export function getMergedCachedData(id) {
  if (!id) return null;
  const cache = readCache();
  const entry = cache[id];
  if (!entry) return null;
  return {
    documentData: entry.documentData ?? null,
    documentInfo: entry.documentInfo ?? null,
  };
}

/** One-time cleanup of legacy cache keys. Runs on module load; never throws. */
(function cleanupLegacyKeys() {
  if (!isBrowser()) return;
  LEGACY_CACHE_KEYS.forEach((key) => {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      // ignore
    }
  });
})();
